import { useState } from "react";

import {
  DATE_RANGE_OPTIONS,
  ORGANIZE_BY_OPTIONS,
  monthWindowForRange,
  monthWindowForTable,
} from "../../constants/filters";
import { COPY } from "../../copy";
import { getActiveProfileToken } from "../../p8AdvertiserData";
import {
  getAdSalesKpiFromRows,
  getAdSpendKpiFromRows,
  getConversionsKpiFromRows,
  getFirstYearRoasKpiFromRows,
  LIFETIME_CHART_WINDOW_SIZE,
  getP8AdPerformanceByMonthRows,
  getP8AdPerformanceByOrderMonthRows,
  getP8ContinuedSalesSeriesForRange,
  getP8MonthlyRoasSeries,
  getP8OrderMonthRoasSeries,
  getP8OrderMonthSalesSeriesForRange,
  getP8OverviewLifetimeLabel,
  getOrderMonthAdSalesKpiFromRows,
  getTotalSalesOverSpendKpiFromRows,
  hasLifetimeMonthCoverage,
  monthRowHasPerformanceData,
} from "../../p8OverviewData";
import type {
  AdsDateRange,
  AdsOrganizeBy,
  PrototypeVersion,
} from "../../types";
import { adTypesTooltip } from "../AdTypesExplainer";
import { useCardPaddingClass, useStackGapClass } from "../CardDensity";
import { InsightsFilterMenu } from "../InsightsFilterMenu";

import { AdPerformanceByMonthTable } from "./AdPerformanceByMonthTable";
import { OverviewChartPair } from "./OverviewCharts";
import { OverviewKpiGrid } from "./OverviewKpiGrid";

type OverviewTabContentProps = {
  version: PrototypeVersion;
  onOpenMonth: (monthIndex: number) => void;
};

export function OverviewTabContent({
  version,
  onOpenMonth,
}: OverviewTabContentProps) {
  const [dateRange, setDateRange] = useState<AdsDateRange>("12m");
  const [v1OrganizeBy, setV1OrganizeBy] =
    useState<AdsOrganizeBy>("ad-click-month");
  const cardPaddingClass = useCardPaddingClass();
  const stackGapClass = useStackGapClass();
  const isV2 = version >= 2;
  const organizeBy: AdsOrganizeBy = isV2 ? "order-month" : v1OrganizeBy;

  const isOrderMonth = organizeBy === "order-month";
  const adClickMonthRows = getP8AdPerformanceByMonthRows();
  const monthRows = isOrderMonth
    ? getP8AdPerformanceByOrderMonthRows()
    : adClickMonthRows;
  const activeProfileToken = getActiveProfileToken();
  const continuedSales = isOrderMonth
    ? getP8OrderMonthSalesSeriesForRange(dateRange)
    : getP8ContinuedSalesSeriesForRange(dateRange);
  // Both ROAS builders map their points one for one, so deriving the series
  // from the whole range and letting each chart window it afterwards gives the
  // same figures a pre-sliced series would.
  const monthlyRoas = isOrderMonth
    ? getP8OrderMonthRoasSeries(continuedSales)
    : getP8MonthlyRoasSeries(continuedSales);
  // Lifetime is the only range that outruns a chart axis. Each card pages its
  // own copy of this window, and both return to the newest months when the
  // advertiser or the range changes.
  const chartMonthWindow = {
    pageable: dateRange === "lifetime",
    windowSize: LIFETIME_CHART_WINDOW_SIZE,
    resetKey: `${activeProfileToken}:${dateRange}`,
  };
  // The date range scopes the headline metrics only. All four are rolled up from
  // the months it selects, so the card reports the chosen period rather than
  // mixing a scoped metric with a lifetime one.
  const kpiWindow = monthWindowForRange(
    dateRange,
    monthRows[0]?.monthIndex ?? 0
  );
  const kpiMonthRows = kpiWindow
    ? monthRows.filter(
        (row) =>
          row.monthIndex >= kpiWindow.from && row.monthIndex <= kpiWindow.to
      )
    : monthRows;
  const tableWindow = monthWindowForTable(
    dateRange,
    monthRows[0]?.monthIndex ?? 0
  );
  const tableMonthRowsForRange = tableWindow
    ? monthRows.filter(
        (row) =>
          row.monthIndex >= tableWindow.from && row.monthIndex <= tableWindow.to
      )
    : monthRows;
  const tableMonthRows = tableMonthRowsForRange.filter(
    monthRowHasPerformanceData
  );
  const visibleTableMonthIndexes = new Set(
    tableMonthRows.map((row) => row.monthIndex)
  );
  const cohortTableMonthRowsForRange = tableWindow
    ? adClickMonthRows.filter(
        (row) =>
          row.monthIndex >= tableWindow.from && row.monthIndex <= tableWindow.to
      )
    : adClickMonthRows;
  const cohortTableMonthRows = cohortTableMonthRowsForRange.filter((row) =>
    visibleTableMonthIndexes.has(row.monthIndex)
  );
  // Every card carries its new/reorder split. There is no page-level ad type to
  // narrow them to one, so the split is always both halves of a real total.
  const useExactSpendAmounts = dateRange === "lifetime";
  const salesKpi = isOrderMonth
    ? {
        ...getOrderMonthAdSalesKpiFromRows(kpiMonthRows),
        ...(version === 3
          ? { tooltip: COPY.overview.kpi.v3TotalOrderMonthSales }
          : {}),
      }
    : {
        ...getAdSalesKpiFromRows(kpiMonthRows, true),
        tooltip: adTypesTooltip(COPY.overview.kpi.totalAdSales),
        interactiveTooltip: true,
      };
  const kpis = [
    salesKpi,
    getAdSpendKpiFromRows(kpiMonthRows, true, useExactSpendAmounts),
    getConversionsKpiFromRows(kpiMonthRows, true),
    isOrderMonth
      ? {
          ...getTotalSalesOverSpendKpiFromRows(
            kpiMonthRows,
            useExactSpendAmounts
          ),
          ...(version === 3
            ? { tooltip: COPY.overview.kpi.v3TotalSalesOverSpend }
            : {}),
        }
      : getFirstYearRoasKpiFromRows(kpiMonthRows, true, useExactSpendAmounts),
  ];
  // Offered only when the months on hand actually cover the whole program.
  // Otherwise the roll-up would silently report a trailing window as the
  // lifetime, which reads as a much smaller advertiser than they are.
  const lifetimeAvailable = hasLifetimeMonthCoverage();
  const dateRangeOptions = DATE_RANGE_OPTIONS.map((option) =>
    option.value === "lifetime"
      ? { ...option, disabled: !lifetimeAvailable }
      : option
  );

  return (
    <div className="space-y-6">
      {/* The date range is the only filter here, and it scopes the whole page.
          Ad type sits on the month table instead: it only ever cut those rows,
          so leaving it up here implied it reached the headline metrics and the
          charts too. */}
      <div className="flex flex-col gap-4 min-[733px]:flex-row min-[733px]:items-start min-[733px]:justify-between">
        <div>
          <h2 className="text-xl leading-8 font-medium text-[#333]">
            {COPY.overview.heading}
          </h2>
          {/* One line, no link. The ad type definitions moved off the end of it
              and onto the ad type filter, which is the one place the two terms
              are a choice the reader has to make. The refresh cadence and
              promotions caveat live down on the month table they describe. */}
          <p className="max-w-[720px] text-sm leading-5 text-[#757575]">
            {version === 3 ? COPY.overview.v3Subhead : COPY.overview.subhead}
          </p>
        </div>
        <div className="flex shrink-0 flex-col gap-2 min-[733px]:flex-row min-[733px]:items-center">
          {!isV2 ? (
            <InsightsFilterMenu
              name="overview-organize-by"
              labelPrefix="Organize by"
              menuTitle="Organize performance by"
              value={organizeBy}
              options={ORGANIZE_BY_OPTIONS}
              onChange={setV1OrganizeBy}
            />
          ) : null}
          <InsightsFilterMenu
            name="overview-date-range"
            labelPrefix="Date range"
            menuTitle="Filter by date range"
            value={dateRange}
            options={dateRangeOptions}
            onChange={setDateRange}
            applyToConfirm
          />
        </div>
      </div>

      <div
        className={`bg-fs-surface-primary rounded-lg border border-[#dfe0e1] ${cardPaddingClass}`}
      >
        <OverviewKpiGrid items={kpis} />
      </div>

      {/* `hasFirstYear` is pinned on: nothing narrows these charts to one ad
          type any more, so the first-year series always have something to
          plot. */}
      <div
        className={`grid items-stretch ${stackGapClass} min-[1366px]:grid-cols-2`}
      >
        {/* Same gate as the month table below, so the cohort page is
            reachable from the charts and the rows in the same views rather
            than from one and not the other. */}
        <OverviewChartPair
          version={version}
          organizeBy={organizeBy}
          continuedSales={continuedSales}
          monthlyRoas={monthlyRoas}
          onOpenMonth={isV2 || !isOrderMonth ? onOpenMonth : undefined}
          monthWindow={chartMonthWindow}
        />
      </div>

      {/* The date range plus the month before it, so last 12 months includes
          this month last year. Ad type still lives on the table. */}
      <AdPerformanceByMonthTable
        version={version}
        organizeBy={organizeBy}
        rows={tableMonthRows}
        cohortRows={isV2 ? cohortTableMonthRows : undefined}
        onOpenMonth={isV2 || !isOrderMonth ? onOpenMonth : undefined}
      />

      <p className="text-xs leading-4 text-[#757575]">
        {getP8OverviewLifetimeLabel()}
      </p>
    </div>
  );
}
